import type { RumEvent } from '@datadog/browser-rum';
import { normalizeApiEndpoint } from './api-endpoint.util';

/**
 * `beforeSend` scrubber for Datadog RUM: removes credentials, emails and
 * presigned-URL signatures from URLs before they leave the browser, and tags
 * API resources with a low-cardinality `endpoint` context.
 * Cheap on purpose: runs once per RUM event.
 */

const SENSITIVE_PARAM = /([?&#])(token|email|code|id_token|access_token|returnUrl)=[^&#]*/g;
const S3_SIGNATURE = /X-Amz-(Signature|Credential|Security-Token)=/i;
const EMAIL_SEGMENT = /@|%40/i;

/** Replaces sensitive query/fragment params (magic link, Cognito callback) with REDACTED. */
function redactParams(url: string): string {
  return url.replace(SENSITIVE_PARAM, '$1$2=REDACTED');
}

function scrubResourceUrl(raw: string): string {
  const queryIdx = raw.indexOf('?');
  const base = queryIdx < 0 ? raw : raw.slice(0, queryIdx);
  const query = queryIdx < 0 ? '' : raw.slice(queryIdx);
  const segments = base.split('/');
  const isSignedAws = (segments[2] ?? '').endsWith('.amazonaws.com') && S3_SIGNATURE.test(query);
  const cleanBase = segments.map((s, i) => (i > 2 && EMAIL_SEGMENT.test(s) ? '{email}' : s)).join('/');
  return cleanBase + (isSignedAws ? '' : redactParams(query));
}

const URL_IN_TEXT = /https?:\/\/[^\s"'<>)]+/g;

/** Scrubs URLs embedded in free text (error message/stack) and bare sensitive params. */
function scrubText(text: string): string {
  return redactParams(text.replace(URL_IN_TEXT, scrubResourceUrl));
}

const stripTrailingSlash = (url: string): string => url.replace(/\/$/, '');

/** Returns false to drop the event. Never throws: a throwing `beforeSend` would drop the event too. */
export function scrubRumEvent(event: RumEvent, apiBaseUrl?: string): boolean {
  try {
    if (event.view) {
      event.view.url = redactParams(event.view.url);
      if (event.view.referrer) event.view.referrer = redactParams(event.view.referrer);
    }

    if (event.type === 'resource') {
      const url = event.resource.url;
      // Connectivity probe to the API root (FE-NOISE-04): not a real API call.
      if (apiBaseUrl && stripTrailingSlash(url) === stripTrailingSlash(apiBaseUrl)) return false;
      if (url.includes('/api/v2/')) {
        event.context = { ...event.context, endpoint: normalizeApiEndpoint(url) };
      }
      event.resource.url = scrubResourceUrl(url);
    } else if (event.type === 'error' && event.error.resource) {
      event.error.resource.url = scrubResourceUrl(event.error.resource.url);
    }
    if (event.type === 'error') {
      if (typeof event.error.message === 'string') event.error.message = scrubText(event.error.message);
      if (typeof event.error.stack === 'string') event.error.stack = scrubText(event.error.stack);
    }
  } catch {
    // Telemetry never breaks the app; keep the event.
  }
  return true;
}
